/* Dynamic Plot code copyright 2020 (c) Tuva Labs Inc. */

export const wrapDynCaseFilter = function(Dyn) {
    let mediator = Dyn.mediator;

    class CaseFilter {
        /*
         Case Filter API:

             Exclude selected cases filter, adds the currently selected cases to the "excluded cases" list, by case index
                 mediator.publish( "records:caseFilterExcludeSelected", {});
             Keep only selected cases filter, adds all but the currently selected cases to the "excluded cases" list, by case index.
                 mediator.publish( "records:caseFilterExcludeSelected", {});
             Categorical attribute filter, excludes the given categories, replacing the filter for that attribute if any:
                 mediator.publish( "records:caseFilterByAttribute",
                     { "attributeID":attrID, "categories": [name1, name2,...] } );
             Numeric attribute filter, values outside range are excluded, replacing the filter for that attribute if any:
                 mediator.publish( "records:caseFilterByAttribute",
                     { "attributeID":attrID, "numeric":{"min":value, "max":value } } );
             Reset one attribute filter:
                 mediator.publish( "records:caseFilterByAttribute",
                     { "attributeID":attrID });
             Reset all filters:
                 mediator.publish( "records:caseFilterReset", {});
         */

        /**
         * Constructor for a case filter manger for the data set.
         * Its job is to accept commands to exclude cases from the plot.
         * @param aDataSet
         * @constructor
         */
        constructor( aDataSet ) {

            // References
            this.dataSet = aDataSet; 	// reference to parent object.

            // Filters
            this.excludedCaseIndices = [];  // array of selected case indices to exclude
            //this.sampledCaseIndices = [];   // array of random case indices to include
            //this.sampledObjs = [];
            this.attributeFilters = {};     // {attributeID: {filterData}} attribute filters used to exclude cases

            // Cases that excluded by above filters (rebuild
            this.caseIsFiltered = []; 	// array of boolean, true if case does not pass filter. Array length may differ from number of cases
            this.numCasesFiltered = 0; // count of (this.caseIsFiltered[] === true)
            this.filterSerialNum = 0;   // unique ID for each filter configuration (0+), so clients know if the have the current filter or not
        }

        save() {
            var filterModel = {};

            // save array of excluded cases
            filterModel.excludedCaseIndices = Dyn.dataUtil.clone(this.excludedCaseIndices);
            //filterModel.sampledCaseIndices = Dyn.dataUtil.clone( this.sampledCaseIndices );
            //filterModel.sampledObjs = Dyn.dataUtil.clone( this.sampledObjs );

            // save attribute filters
            filterModel.attributeFilters = Dyn.dataUtil.clone(this.attributeFilters);

            return filterModel;
        }

        /**
         * Restore a plot filter saved with this.save()
         * @param savedData
         * @param deferPlotUpdate {Boolean}- optional, if true, don't update plot after filter change
         */
        restore( savedData, deferPlotUpdate ) {
            var hasFilter = false;

            // restore filter data
            if( savedData.excludedCaseIndices ){
                this.excludedCaseIndices = Dyn.dataUtil.clone(savedData.excludedCaseIndices);
                hasFilter = true;
            }
            //if( savedData.sampledCaseIndices ) {
            //    this.sampledCaseIndices = Dyn.dataUtil.clone( savedData.sampledCaseIndices );
            //    this.sampledObjs = Dyn.dataUtil.clone( savedData.sampledObjs );
            //    hasFilter = true;
            //}
            if( savedData.attributeFilters ){
                this.attributeFilters = Dyn.dataUtil.clone(savedData.attributeFilters);
                hasFilter = true;
            }

            // apply filters
            if( hasFilter ){
                this.applyFilters( deferPlotUpdate );
            } else {
                this.resetAll( deferPlotUpdate );
            }
        }

        /**
         * Reset filter for a given attribute id.
         * @param {String} [attributeId]
         */
        resetFilterByAttributeId(attributeId) {
            if( this.attributeFilters[attributeId] ){
                delete this.attributeFilters[attributeId];
                this.applyFilters();
            }
        }

        /**
         * Reset case filtering.
         * @param {boolean} [deferPlotUpdate] -- set to true to notify plot that it should not update yet.
         */
        resetAll(deferPlotUpdate) {

            // Only publish the message if there is filter applied
            if( this.numCasesFiltered > 0 ){
                // clear all filters
                this.excludedCaseIndices = [];
                //this.sampledCaseIndices = [];
                //this.sampledObjs = [];
                this.attributeFilters = {};
                this.caseIsFiltered = [];
                this.numCasesFiltered = 0;
                this.filterSerialNum++;	// filter has changed so increment the serial number

                // inform other objects of filter change
                mediator.publish("records:setCasesFiltered", {
                    id: this.filterSerialNum,
                    resetAll: true,
                    deferPlotUpdate: deferPlotUpdate
                });
            }
        }

        /**
         * Add to case filtering, by excluding the currently selected cases or categories.
         */
        excludeSelected() {
            var excludedCasesArray = this.excludedCaseIndices, // values are case indices
                selectedCasesArray = this.dataSet.selectedCases,
                selectedCategories = this.dataSet.selectedCategories,
                selectedCategoryAttribute = this.dataSet.getSelectedCategoryAttribute();

            if( selectedCategories.length ){
                var filter = this.getAttributeFilter(selectedCategoryAttribute.id);
                if( filter ){
                    // get union of existing filter and selected categories
                    selectedCategories.forEach( value => {
                        if( ! filter.categories.includes(value)){
                            filter.categories.push(value);
                        }
                    });
                } else {
                    // create new attribute filter
                    filter = {
                        attributeID: selectedCategoryAttribute.id,
                        categories: Dyn.dataUtil.clone(selectedCategories)
                    };
                }
                this.filterByAttribute(filter);
            } else {
                // add the selected cases to the filter (keep existing filtered cases)
                selectedCasesArray.forEach( value => {
                    if( ! excludedCasesArray.includes(value)){
                        //console.log("caseFilterExcludeSelected caseIndex ="+value);
                        excludedCasesArray.push(value); // selected case not yet in excluded cases list, add it
                    }
                });
                this.applyFilters();
            }
        }

        /**
         * update case filtering, by keeping only the currently selected cases or categories (filter out others).
         */
        keepOnlySelected() {
            let selectedCategories = this.dataSet.selectedCategories,
                selectedCategoryAttribute = this.dataSet.getSelectedCategoryAttribute();

            if( selectedCategories.length ){
                let filter = this.getAttributeFilter(selectedCategoryAttribute.id),
                    allCategories = selectedCategoryAttribute.statsPreFilter.categories,
                    activeCategories = selectedCategoryAttribute.stats.categories;
                if( selectedCategoryAttribute.hasNumericIntervals()) {
                    // use intervals as categories if plot made intervals (e.g. pie chart)
                    activeCategories = allCategories = selectedCategoryAttribute.numericIntervals.categories;
                }
                if( selectedCategoryAttribute.stats.missingCaseCount > 0 ) {
                    // exclude any missing values if present; treat it as an extra category
                    allCategories = allCategories.concat( [Dyn.dataUtil.kMissingValue] );
                    activeCategories = allCategories.concat( [Dyn.dataUtil.kMissingValue] );
                }

                if( filter ){
                    // get union of existing filter and non-selected categories
                    activeCategories.forEach( value => {
                        if( ! selectedCategories.includes(value)) {
                            filter.categories.push(value);
                        }
                    });
                } else {
                    // create new attribute filter
                    var filterByCategories = [];

                    // get non-selected categories from allCategories
                    allCategories.forEach( value => {
                        if( ! selectedCategories.includes(value)){
                            filterByCategories.push(value);
                        }
                    });

                    filter = {
                        attributeID: selectedCategoryAttribute.id,
                        categories: filterByCategories
                    };
                }
                this.filterByAttribute(filter);

            } else {
                this.excludedCaseIndices = [];
                let excludedCasesArray = this.excludedCaseIndices,
                    thisDataSet = this.dataSet;
                for( let i = 0, j = this.dataSet.getNumCasesPreFilter(); i < j; i++ ){
                    if( !thisDataSet.isCaseSelected(i) ){
                        excludedCasesArray.push(i);
                    }
                }
                this.applyFilters();
            }

        }

        /**
         * Remove the excludeSelected() or keepOnlySelected() filter.
         * See also resetAll() and filterByAttribute()
         */
        resetFilterByExcludedCases() {

            this.excludedCaseIndices = [];
            this.applyFilters();
        }

        /**
         * Add to case filtering, by excluding categories or a numeric range of the given attribute.
         * Or remove the matching attribute's filter, if neither categories nor numeric parameters are present.
         * @param filterData
         * @param {string} filterData.attributeID -- attribute ID string
         * @param {Array}  filterData.categories -- array of category values
         * @param {number} filterData.numeric.min -- minimum numeric value (lower values are filtered out)
         * @param {number} filterData.numeric.max -- maximum numeric value (higher values are filtered out)
         * @param {boolean} [deferPlotUpdate] -- set to true to notify plot that it should not update yet.
         */
        filterByAttribute( filterData, deferPlotUpdate) {

            var attribute = this.dataSet.getAttributeByID( filterData.attributeID );

            if( attribute ){
                if( filterData.categories || filterData.numeric ){
                    // save the filter data by attribute ID
                    this.attributeFilters[attribute.id] = filterData;
                } else {
                    // clear the filter for this attribute ID
                    var wasDeleted = delete this.attributeFilters[attribute.id];
                    Dyn.assert(wasDeleted, "filterByAttribute() unable to delete attribute" + attribute.id);
                }
            }
            this.applyFilters( deferPlotUpdate );
        }

        /**
         * Apply the current set of filters to the current case data.
         * Result is this.filterCases[] is true for each corresponding case that is filtered out of the plot.
         * To be used each time the filter changes.
         * Clients should use Dyn.DataSet.isCaseFilteredOut() to test each case index.
         * @param {boolean} [deferPlotUpdate] -- set to true to notify plot that it should not update yet.
         */
        applyFilters( deferPlotUpdate ) {
            var i, j, isFiltered;

            // reset case counts for each attribute filter if any
            this._clearCaseCounts();

            // test each case against filter, set to true if does not pass filter
            //		also increments case counts.
            this.numCasesFiltered = 0;
            for( i = 0, j = this.dataSet.getNumCasesPreFilter(); i < j; i++ ){
                this.caseIsFiltered[i] = isFiltered = this._caseFilteredOut(i);
                if( isFiltered ){
                    ++(this.numCasesFiltered);
                }
            }
            this.caseIsFiltered.length = j; // remove values after last case
            this.filterSerialNum++;	// filter has changed so increment the serial number

            // inform other objects of filter change
            mediator.publish("records:setCasesFiltered", {
                id: this.filterSerialNum,
                deferPlotUpdate: deferPlotUpdate
            });
        }

        /**
         * Private function to clear the case count for each filter
         * @private
         */
        _clearCaseCounts() {
            var attributeID, attributeFilter;

            // test against each attribute filter, which are stored by attribute ID.
            for( attributeID in this.attributeFilters ){
                if( this.attributeFilters.hasOwnProperty(attributeID) ){
                    attributeFilter = this.attributeFilters[attributeID];
                    attributeFilter.caseCount = 0;
                }
            }
        }

        /**
         * Private function to test a single case index against all current filters.
         * @param caseIndex
         * @returns {boolean}  -- true if case is excluded by the filter
         * @private
         */
        _caseFilteredOut(caseIndex, excludeRandomSample) {
            var attributeID, attributeFilter;
            //hasRandomSampleFilter = (this.sampledCaseIndices.length >= 1);

            // test against excluded case indices
            if( this.excludedCaseIndices.includes(caseIndex) ){
                return true; // caseIndex is filtered out
            }
            // test against random sample indices
            // added condition to get population array before random sample
            //if (!excludeRandomSample && hasRandomSampleFilter && ! this.sampledCaseIndices.includes( caseIndex ) ) {
            //    return true; // caseIndex not in sample: case is filtered out
            //}
            // test against each attribute filter, which are stored by attribute ID.
            for( attributeID in this.attributeFilters ){
                if( this.attributeFilters.hasOwnProperty(attributeID) ){
                    attributeFilter = this.attributeFilters[attributeID];
                    if( typeof attributeFilter === "object" &&
                        this._caseFilteredByAttribute(attributeFilter, caseIndex) ){
                        ++attributeFilter.caseCount;
                        return true; // caseIndex is filtered out
                    }
                }
            }
            return false; // passes all filter tests, case is not filtered out
        }

        /**
         * Private function to test a single case index against one attribute filter.
         * @param {*} attrFilter
         * @param {*} attrFilter.categories -- [] category values which the filter excludes.
         * @param {*} attrFilter.numeric -- {min,max} numeric range which passes the filter.
         * @param caseIndex
         * @returns {boolean} -- true if case is excluded by the filter
         * @private
         */
        _caseFilteredByAttribute(attrFilter, caseIndex) {
            var attribute = this.dataSet.getAttributeByID(attrFilter.attributeID),
                caseValue = attribute.cases[caseIndex];
            if( attrFilter.categories ){
                if( attribute.hasNumericIntervals()) {
                    caseValue = attribute.getNumericIntervalString(caseIndex);
                }
                return attrFilter.categories.includes( caseValue ); // case matches a filter category
            } else if( attrFilter.numeric && ((typeof caseValue) === "number") ){
                return !Dyn.mathUtil.isInRange(caseValue, attrFilter.numeric.min, attrFilter.numeric.max);
            }
            return false; // passes filter
        }

        /**
         * Get the attribute filter associated with the given attribute ID, or null if it does not exist.
         * @param attributeID
         * @returns {*} -- returns filter with { attributeID, categories:[], numeric:{min,max} }
         */
        getAttributeFilter(attributeID) {
            var property = this.attributeFilters[attributeID];
            if( this.attributeFilters.hasOwnProperty(attributeID) &&
                (typeof property === "object") ){
                return property;
            }
            return null;
        }

        getNumCasesFiltered() {
            return this.numCasesFiltered;
        }

        /** get array of case indices in the dataset, after filtering. */
        getCasesAfterFilter() {
            var caseArray = [];
            for( let i = 0, j = this.dataSet.getNumCasesPreFilter(); i < j; i++ ){
                if( !this.caseIsFiltered[i] ){
                    caseArray.push(i);
                }
            }
            return caseArray;
        }

        getNumCasesExcludedBySelection() {
            return this.excludedCaseIndices.length;
        }

        //getNumCasesInSample() {
        //
        //    if (this.sampledObjs.length > 0){
        //        return this.sampledObjs[this.sampledObjs.length-1]['data'].length;
        //    } else {
        //        return 0;
        //    }
        //}

        /**
         * Get the number of cases excluded by the given attribute, which will be 0 if no filter.
         * Note 1: when a case is excluded by multiple filters, _caseFilteredOut() will determine
         * which filter gets the case count, so a filter may exclude 0 cases and still be a valid filter.
         * Note 2: this count is only valid at end of filterByAttribute() call (updated each time by applyFilters()).
         * @param attributeID
         * @returns {number}
         */
        getNumCasesExcludedByAttribute(attributeID) {
            var attributeFilter = this.getAttributeFilter(attributeID);
            return attributeFilter ? attributeFilter.caseCount : 0;
        }

        /**
         * Get a unique ID for this configuration of filtered cases, so clients know when filter has changed.
         * @returns {number}
         */
        getID() {
            return this.filterSerialNum;
        }
    } // class CaseFilter
    Dyn.CaseFilter = CaseFilter;

    class MissingCaseManager {
        /**
         * Constructor for a missing case manger for the data set.  It's job is to accept commands
         * to cases from the plot, based on missing values in the attributes used by the plot.
         * @param aDataSet
         * @constructor
         */
        constructor( aDataSet ) {

            // References
            this.dataSet = aDataSet; 	// reference to parent object.

            // State
            this.caseIsMissing = []; 	// array of boolean, true if case does not pass filter. Array length may differ from number of cases
            this.numCasesMissing = 0; // count of (this.caseIsMissing[] === true)
            this.missingSerialNum = 0;   // unique ID for each filter configuration (0+), so clients know if the have the current filter or not
        }

        /**
         * Set or clear the "case is missing" flag, which shows that the plot is excluding a case
         * because it does not have a plottable value on one or more of the attributes used by the plot.
         *
         * NOTE: assumes cases are set in order, to get a correct case count.
         * @param caseIndex
         * @param inIsMissing
         */
        setCaseIsMissing(caseIndex, inIsMissing) {
            var tChanged = (this.caseIsMissing[caseIndex] !== inIsMissing);

            // reset count when setting 0th case (WARNING: assumes cases are set in order, 0+)
            if( caseIndex === 0 ){
                this.numCasesMissing = 0;
            }
            // update count of missing cases
            if( inIsMissing ){
                this.numCasesMissing++;
            }
            if( tChanged ){
                this.caseIsMissing[caseIndex] = inIsMissing;
                this.missingSerialNum++; // configuration has changed so increment the serial number
            }
            //console.log("setCaseIsMissing caseIndex="+caseIndex+" missing="+inIsMissing+" numCasesMissing="+this.numCasesMissing );
        }

        clearAllMissingCases(caseIndex, inIsMissing) {
            var tChanged = (this.numCasesMissing !== 0);
            this.caseIsMissing = [];
            this.numCasesMissing = 0;
            if( tChanged ){
                this.missingSerialNum++; // configuration has changed so increment the serial number
            }
        }

        /**
         * Get a unique ID for this configuration of missing cases, so clients know when missing case configuration has changed.
         * @returns {number}
         */
        getID() {
            return this.missingSerialNum;
        }

        getNumCasesMissing() {
            return this.numCasesMissing;
        }
    } // class MissingCaseManager
    Dyn.MissingCaseManager = MissingCaseManager;

} // wrapDynCaseFilter

